import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { URI, Utils } from 'vscode-uri';
import { filter } from 'fuzzaldrin-plus';
import { AbstractAIContextProvider } from '../ai_context_provider';
import { DefaultRepositoryService, RepositoryService } from '../../services/git/repository_service';
import { RepositoryFile } from '../../services/git/repository';
import { DuoProjectAccessChecker } from '../../services/duo_access';
import { DuoProjectStatus } from '../../services/duo_access/project_access_checker';
import { log } from '../../log';
import { EmptyFileResolver, FileResolver } from '../../services/fs';
import { getRelativePath } from '../../services/fs/utils';
import { AIContextItem, AIContextItemMetadata, AIContextSearchQuery } from '..';

type LocalFileMetadata = AIContextItemMetadata & {
  enabled: boolean;
  disabledReasons?: string[];
  subType: 'local_file_search';
  relativePath?: string;
  workspaceFolder?: WorkspaceFolder;
  project: string;
};

export type LocalFileAIContextItem = AIContextItem & {
  category: 'file';
  metadata: LocalFileMetadata;
};

export interface LocalFilesContextProvider
  extends AbstractAIContextProvider<LocalFileAIContextItem> {}

export const LocalFilesProvider = createInterfaceId<LocalFilesContextProvider>(
  'LocalFileSearchContextProvider',
);

// TODO: Make this configurable
// https://gitlab.com/gitlab-org/gitlab/-/issues/490602
const MAX_RESULTS = 100;

@Injectable(LocalFilesProvider, [RepositoryService, DuoProjectAccessChecker, FileResolver])
export class DefaultLocalFileContextProvider
  extends AbstractAIContextProvider<LocalFileAIContextItem>
  implements LocalFilesContextProvider
{
  #repositoryService: DefaultRepositoryService;

  #projectAccessChecker: DuoProjectAccessChecker;

  #fileResolver: FileResolver;

  constructor(
    repositoryService: DefaultRepositoryService,
    projectAccessChecker: DuoProjectAccessChecker,
    fileResolver: EmptyFileResolver,
  ) {
    super('local_file_search');
    this.#repositoryService = repositoryService;
    this.#projectAccessChecker = projectAccessChecker;
    this.#fileResolver = fileResolver;
  }

  async searchContextItems(query: AIContextSearchQuery): Promise<LocalFileAIContextItem[]> {
    log.info(`[LocalFilesContextProvider] Searching for ${query.query}`);
    if (query.query.trim() === '') {
      return [];
    }

    const allFilesMap: Map<string, RepositoryFile> = new Map();
    for (const folder of query.workspaceFolders) {
      const repositoryFiles = this.#repositoryService.getCurrentFilesForWorkspace(folder.uri, {
        excludeGitFolder: true,
        excludeIgnored: true,
      });
      for (const file of repositoryFiles) {
        allFilesMap.set(file.uri.fsPath, file);
      }
    }

    log.info(`[LocalFilesContextProvider] ${allFilesMap.size} total files`);

    const fileNames = Array.from(allFilesMap.keys());
    const results = filter(fileNames, query.query, { maxResults: MAX_RESULTS });
    log.info(
      `[LocalFilesContextProvider] Found ${results.length} results. Max allowed: ${MAX_RESULTS}`,
    );

    const itemsWithoutContent: LocalFileAIContextItem[] = [];

    for (const result of results) {
      const file = allFilesMap.get(result);

      if (file) {
        const { project: projectFromChecker, status } =
          this.#projectAccessChecker.checkProjectStatus(file.uri.toString(), file.workspaceFolder);
        const enabled = status !== DuoProjectStatus.DuoDisabled;
        const project = projectFromChecker;
        const item: LocalFileAIContextItem = {
          id: file.uri.toString(),
          category: 'file',
          metadata: {
            title: Utils.basename(file.uri),
            enabled,
            project: project?.namespaceWithPath ?? 'not a GitLab project',
            disabledReasons: enabled ? [] : ['project disabled'],
            subType: 'local_file_search',
            relativePath: getRelativePath(URI.parse(file.workspaceFolder.uri), file.uri),
            workspaceFolder: file.workspaceFolder,
          },
        };

        itemsWithoutContent.push(item);
      }
    }

    return itemsWithoutContent;
  }

  async retrieveSelectedContextItemsWithContent(): Promise<LocalFileAIContextItem[]> {
    const items = this.getSelectedContextItems();

    const itemsWithContentPromises = items.map(async (item) => {
      const content = await this.#fileResolver.readFile({
        fileUri: URI.parse(item.id),
      });
      return {
        ...item,
        content,
      };
    });

    return Promise.all(itemsWithContentPromises);
  }
}
